import React from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaBrain, FaChartLine, FaCogs, FaDatabase, FaRocket, FaCheckCircle, FaQuoteLeft, FaRobot } from 'react-icons/fa';
import InteractiveNetwork from '../components/InteractiveNetwork';
import useDocumentHead from '../hooks/useDocumentHead';
import './Home.css';

const SERVICES = [
    { icon: <FaRobot />, title: 'AI Agents for Business', desc: 'Custom intelligent agents that automate complex workflows, make decisions, and operate autonomously to scale your operations.' },
    { icon: <FaCogs />, title: 'Custom AI Tool Creation', desc: 'Purpose-built AI tools and integrations designed for your specific business processes—from data processing to customer interaction.' },
    { icon: <FaBrain />, title: 'LLM & Generative AI', desc: 'Fine-tuned language models, RAG systems, and generative AI solutions that understand your domain and deliver real results.' },
    { icon: <FaChartLine />, title: 'Agentic Workflows', desc: 'Multi-agent orchestration systems that coordinate AI agents to handle end-to-end business processes autonomously.' },
    { icon: <FaDatabase />, title: 'Data & AI Infrastructure', desc: 'Robust data pipelines and AI infrastructure to power your agents, tools, and analytics initiatives.' },
    { icon: <FaRocket />, title: 'AI Deployment & Scaling', desc: 'Take your AI agents and tools from prototype to production with monitoring, optimization, and seamless scaling.' },
];

const STATS = [
    { number: '🚀', label: 'Rapid Prototyping' },
    { number: '🤖', label: 'AI-First Approach' },
    { number: '🔧', label: 'Custom-Built Tools' },
    { number: '📈', label: 'Measurable Impact' },
];

const TESTIMONIALS = [
    { quote: "The AI agents Tesseractive built for us handle 80% of our customer onboarding automatically. It's like having a tireless team member who never sleeps.", author: 'Sarah Chen', role: 'CTO, TechVentures Inc.' },
    { quote: "Ivan's team built custom AI tools that transformed our operations. What used to take our analysts days now happens in minutes with their intelligent workflow agents.", author: 'Marcus Johnson', role: 'VP Data, FinanceCorps' },
    { quote: "The multi-agent system Ivan designed for us orchestrates our entire data pipeline—ingesting, processing, and reporting on millions of records daily with 99.9% accuracy.", author: 'Dr. Emily Park', role: 'Head of Research, BioAnalytics' },
];

const Home = () => {
    useDocumentHead({
        title: 'Tesseractive Inc. | AI Agents, Custom AI Tools & Data Science Consulting',
        description: 'Expert AI agent development and custom AI tool creation by Ivan Smith. We build intelligent agents, automated workflows, and AI-powered tools that transform how businesses operate.',
        canonical: 'https://tesseractive.datavisionlabs.ai/',
        ogImage: 'https://tesseractive.datavisionlabs.ai/logo512.png',
    });

    return (
        <div className="home">
            {/* Hero Section */}
            <section className="hero">
                <div className="hero-bg">
                    <div className="hero-grid-pattern"></div>
                    <div className="hero-glow hero-glow-1"></div>
                    <div className="hero-glow hero-glow-2"></div>
                </div>
                <div className="container hero-content">
                    <div className="hero-text">
                        <div className="hero-badge">
                            <FaRobot /> AI Agents & Custom Tool Builders
                        </div>
                        <h1><span className="gradient-text">Tesseractive</span></h1>
                        <p className="hero-tagline">a <a href="https://datavisionlabs.ai/" target="_blank" rel="noopener noreferrer" className="datavision-link">DataVision Labs</a> company</p>
                        <p className="hero-subtitle">
                            We build custom AI agents and intelligent tools that automate your business operations,
                            accelerate decision-making, and unlock new revenue streams. From autonomous workflows
                            to production-ready AI systems—we turn AI into your competitive advantage.
                        </p>
                        <div className="hero-buttons">
                            <Link to="/contact" className="btn btn-primary">
                                Schedule a Consultation <FaArrowRight />
                            </Link>
                            <Link to="/case-studies" className="btn btn-secondary">
                                View Case Studies
                            </Link>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <InteractiveNetwork />
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section">
                <div className="container">
                    <div className="stats-grid">
                        {STATS.map((stat) => (
                            <div key={stat.label} className="stat-card">
                                <span className="stat-number">{stat.number}</span>
                                <span className="stat-label">{stat.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="section services-section">
                <div className="container">
                    <div className="section-header">
                        <h2>Our Services</h2>
                        <p>Custom AI agents, intelligent tools, and data science solutions to automate and accelerate your business</p>
                    </div>
                    <div className="services-grid">
                        {SERVICES.map((service, i) => (
                            <div key={service.title} className="card service-card" style={{ animationDelay: `${i * 0.1}s` }}>
                                <div className="service-icon">{service.icon}</div>
                                <h3>{service.title}</h3>
                                <p>{service.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div className="services-cta">
                        <Link to="/services" className="btn btn-secondary">
                            Explore All Services <FaArrowRight />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="section why-section">
                <div className="container">
                    <div className="why-grid">
                        <div className="why-content">
                            <h2>Why Choose <span className="gradient-text">Tesseractive?</span></h2>
                            <p className="why-intro">
                                With deep expertise in building AI systems and a sharp focus on intelligent agents
                                and custom AI tools, Ivan Smith brings the know-how to turn cutting-edge AI into real
                                business outcomes.
                            </p>
                            <div className="why-points">
                                <div className="why-point">
                                    <FaCheckCircle className="why-check" />
                                    <div>
                                        <h4>Cross-Industry Experience</h4>
                                        <p>Successful projects across industries including finance, healthcare, retail, and technology.</p>
                                    </div>
                                </div>
                                <div className="why-point">
                                    <FaCheckCircle className="why-check" />
                                    <div>
                                        <h4>End-to-End Agent Development</h4>
                                        <p>From agent design and prototyping to production deployment, monitoring, and continuous optimization.</p>
                                    </div>
                                </div>
                                <div className="why-point">
                                    <FaCheckCircle className="why-check" />
                                    <div>
                                        <h4>Business-First Approach</h4>
                                        <p>We focus on delivering measurable ROI, not just building impressive technology.</p>
                                    </div>
                                </div>
                                <div className="why-point">
                                    <FaCheckCircle className="why-check" />
                                    <div>
                                        <h4>Cutting-Edge Agent & AI Expertise</h4>
                                        <p>Stay ahead with the latest in AI agents, LLMs, agentic workflows, tool-use frameworks, and more.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="why-visual">
                            <div className="why-card-stack">
                                <div className="why-float-card card-1">
                                    <FaRobot /> AI Agents
                                </div>
                                <div className="why-float-card card-2">
                                    <FaCogs /> Custom AI Tools
                                </div>
                                <div className="why-float-card card-3">
                                    <FaBrain /> LLMs & GenAI
                                </div>
                                <div className="why-float-card card-4">
                                    <FaRocket /> Agentic Workflows
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="section testimonials-section">
                <div className="container">
                    <div className="section-header">
                        <h2>What Clients Say</h2>
                        <p>Trusted by leading companies to deliver transformative AI solutions</p>
                    </div>
                    <div className="testimonials-grid">
                        {TESTIMONIALS.map((t) => (
                            <div key={t.author} className="card testimonial-card">
                                <FaQuoteLeft className="quote-icon" />
                                <p className="quote-text">{t.quote}</p>
                                <div className="quote-author">
                                    <div className="author-avatar">{t.author.charAt(0)}</div>
                                    <div>
                                        <strong>{t.author}</strong>
                                        <span>{t.role}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta-section">
                <div className="container">
                    <div className="cta-content">
                        <h2>Ready to Build AI Agents for Your Business?</h2>
                        <p>Let's discuss how custom AI agents and tools can automate your operations and unlock new growth.</p>
                        <Link to="/contact" className="btn btn-primary">
                            Get Started Today <FaArrowRight />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
